'use client';

import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ExternalLink, Loader2, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  createApplicationAction,
  reopenApplicationAction,
} from '@kit/auditions/server/actions';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { AnonymousApplyCta } from '~/_components/anonymous-apply-cta';
import {
  buildAuthUrlForApply,
  pendingAuditionIntent,
} from '~/_lib/apply-journey';
import { useBrand } from '~/components/brand-provider';
import {
  isCrossBrand,
  resolveCrossBrandHref,
} from '~/lib/routing/resolve-app-url';
import { isSafeExternalHref } from '~/lib/safe-external-href';
import { AuditionExternalLink } from './audition-external-link';

interface CompetitionApplySectionProps {
  auditionId: string;
  /** Used to seed the apply-journey intent when an anonymous user clicks Apply. */
  auditionTitle: string;
  /**
   * Whether the competition has any style entries. The public rail no longer
   * picks styles (that moved into the in-form `competition_styles` field on the
   * apply page); this flag only gates a one-line "select styles after you
   * register" affordance is unnecessary — kept solely so the CTA copy can read
   * "Register" for competitions with styles vs a plain "Apply" otherwise.
   */
  hasStyleEntries?: boolean;
  /**
   * Whether the underlying audition is a competition (`auditions.is_competition`).
   * Drives scholarship CTA wording ("Register for Scholarship") — competitions
   * are scholarship programmes, non-competition auditions keep "Register"/"Apply".
   */
  isCompetition?: boolean;
  isOpen: boolean;
  closingDate?: string | null;
  applicationFeeAmount?: number | null;
  applicationFeeCurrency?: string | null;
  externalLink?: string | null;
  byInvitationOnly?: boolean;
  /** True when the audition has an `access_code` set. Dancers can self-
   * redeem the code to create their own invitation. */
  hasAccessCode?: boolean;
  /**
   * Shared access code read from the page URL (`?code=`), carried by the
   * emailed competition invite link. When present on an invite-only
   * competition, the rail auto-redeems it on mount instead of showing the
   * manual entry. Absent → "By invitation only" message.
   */
  urlAccessCode?: string;
  /**
   * The current viewer's invitation status for this audition. Only consulted
   * when `byInvitationOnly` is true. Resolved server-side by
   * `AuditionInvitationService.findStatusForUser`. Drives the CTA caption
   * matrix per D8 in WIP_gating_competition_apply_by_invitation_2026_05_22.md.
   */
  viewerInvitationStatus?: 'active' | 'revoked' | 'none';
  isAuthenticated: boolean;
  /**
   * The user's existing registration for this audition, when one exists.
   * One registration per dancer per competition — when provided, the rail
   * short-circuits the style picker and routes the dancer to the right
   * surface: the multi-step wizard for editable statuses (open / issues /
   * submitted) and reopen (archived).
   */
  existingApplication?: {
    id: string;
    status: string;
  } | null;
  /**
   * Whether the organizer permits entrants to edit their registration after
   * acceptance (`auditions.allow_accepted_edits`, default true). When true an
   * `accepted` registration surfaces an "Update your registration" CTA into the
   * wizard; when false it stays a status-only box.
   */
  allowAcceptedEdits?: boolean;
  slug: string;
  /**
   * True when the page URL carries `?apply=true` — the apply-journey signal
   * emitted by the anonymous CTA, the sign-in/sign-up links, and the resume
   * banner (`buildApplyJourneyNext`). When an authenticated dancer lands here
   * with this flag set (e.g. after clicking "Already have an account? Sign in"
   * and logging in), the section auto-triggers registration instead of leaving
   * them on a static page with their intent silently dropped. Without a
   * consumer for this flag the whole journey loses its last hop after auth.
   */
  autoApply?: boolean;
}

/**
 * Editorial apply section — chrome-less variant of the original CompetitionApplySection.
 * The parent (`PublicAuditionView` right rail) provides the surface and metadata; this
 * component owns only the style-entry picker + button state.
 */
export function CompetitionApplySection({
  auditionId,
  auditionTitle,
  hasStyleEntries = false,
  isCompetition = false,
  isOpen,
  closingDate: _closingDate,
  externalLink,
  byInvitationOnly = false,
  hasAccessCode = false,
  urlAccessCode,
  viewerInvitationStatus = 'none',
  isAuthenticated,
  existingApplication,
  allowAcceptedEdits = true,
  slug,
  autoApply = false,
}: CompetitionApplySectionProps) {
  const router = useRouter();
  const brand = useBrand();
  const { t } = useTranslation();
  const [isApplying, setIsApplying] = useState(false);
  const [isReopening, startReopen] = useTransition();

  // BAL-285: competition surfaces read "Register for Scholarship"; other
  // surfaces keep the style-aware "Register"/"Apply" wording.
  const ctaLabel = isCompetition
    ? t('auditions:apply.ctaScholarship')
    : hasStyleEntries
      ? t('auditions:apply.ctaRegister')
      : t('auditions:apply.ctaApply');

  // Public code gate (BAL — GDO public-stage gate): a code-gated
  // competition must NOT let an anonymous visitor reach sign-up until a valid
  // access code is supplied (typed, or carried in the page URL as `?code=`).
  // `verifiedCode` holds the code that passed validation this session — null
  // until verified. We keep the actual string (not just a boolean) so a
  // manually-typed code can ride into the signup journey exactly like a `?code=`
  // link does, where it auto-redeems post-login.
  const requiresPublicCode = byInvitationOnly && hasAccessCode;
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);
  const isCodeVerified = verifiedCode !== null;
  // The code to carry forward into the signup journey: a verified typed code
  // wins, otherwise whatever arrived in the URL.
  const effectiveCode = verifiedCode ?? urlAccessCode;

  // `?code=` auto-verify: any visitor (logged in or not) landing on a
  // code-gated competition via the emailed invite link
  // (`/competitions/<slug>?code=...`) has the code validated on mount so the
  // Register CTA unlocks without a manual step. Verify only — never redeems
  // (unlocking must not require a dancer profile). The verified code rides
  // into registration (`effectiveCode`), where create-application redeems it.
  const canAutoVerifyCode =
    requiresPublicCode &&
    !isCodeVerified &&
    viewerInvitationStatus !== 'active' &&
    !!urlAccessCode?.trim();
  useEffect(() => {
    if (!canAutoVerifyCode || !urlAccessCode) return;
    let cancelled = false;
    void (async () => {
      try {
        const { verifyAccessCodeAction } =
          await import('@kit/auditions/server/actions');
        const result = await verifyAccessCodeAction({
          auditionId,
          code: urlAccessCode.trim(),
        });
        if (cancelled) return;
        if (result.success && result.data.valid) {
          setVerifiedCode(urlAccessCode.trim());
        }
        // Invalid URL code → stay gated; the manual entry box lets them retype.
      } catch {
        // Network/transient error → stay gated.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAutoVerifyCode, urlAccessCode, auditionId]);

  // Manual access-code entry: the emailed link carries `?code=`, but a dancer
  // who only received the code itself (or whose link lost the query) needs a
  // way to type it in.
  const [manualCode, setManualCode] = useState('');

  // Manual code entry: validate (no redemption, no profile required) and, on
  // success, unlock the Register CTA carrying the typed code into registration.
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const handleVerifyManualCode = async () => {
    const code = manualCode.trim();
    if (!code || isVerifyingCode) return;
    setIsVerifyingCode(true);
    try {
      const { verifyAccessCodeAction } =
        await import('@kit/auditions/server/actions');
      const result = await verifyAccessCodeAction({ auditionId, code });
      if (result.success && result.data.valid) {
        setVerifiedCode(code);
        toast.success(t('auditions:apply.toastCodeAccepted'));
      } else {
        toast.error(t('auditions:apply.toastInvalidCode'));
      }
    } catch {
      toast.error(t('auditions:apply.toastInvalidCode'));
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleApply = useCallback(async () => {
    if (!isAuthenticated) {
      pendingAuditionIntent.write({
        surface: 'competition',
        slug,
        title: auditionTitle,
        ...(urlAccessCode ? { code: urlAccessCode } : {}),
      });
      router.push(
        buildAuthUrlForApply(
          {
            surface: 'competition',
            slug,
            ...(urlAccessCode ? { code: urlAccessCode } : {}),
          },
          'signup',
        ),
      );
      return;
    }

    setIsApplying(true);
    try {
      // Styles are now selected inside the registration form (the in-form
      // `competition_styles` field), not on this public page — so the apply
      // action creates the registration with no style entries; the dancer
      // picks their styles on the /apply form and submit persists them.
      // Carry the unlocked code so create-application redeems it (creating the
      // invitation) for a code-gated competition — redemption at register, not
      // at unlock.
      const result = await createApplicationAction({
        auditionId,
        ...(effectiveCode ? { accessCode: effectiveCode.trim() } : {}),
      });

      if (result.success) {
        // A draft now exists → the DB-backed resume banner takes over; drop the
        // pre-draft localStorage intent so the two never both show (BAL-319).
        pendingAuditionIntent.clear();
        toast.success(t('auditions:apply.toastRegistrationStarted'), {
          id: `apply-${auditionId}`,
        });
        const params = new URLSearchParams({
          applicationId: result.data.id,
        });
        // Competitions are Ballee-owned; if this audition view is served on
        // Flow, route the apply page through the cross-brand handoff (a hard
        // nav, since the handoff is an API route). On Ballee it stays client nav.
        const applyPath = `/competitions/${slug}/apply?${params.toString()}`;
        if (isCrossBrand(applyPath, brand)) {
          window.location.assign(resolveCrossBrandHref(applyPath, brand));
        } else {
          router.push(applyPath);
        }
      } else {
        const errorMessage = result.error;
        if (errorMessage.includes('already')) {
          toast.info(t('auditions:apply.toastAlreadyRegistered'), {
            id: `apply-${auditionId}`,
          });
        } else {
          toast.error(errorMessage, { id: `apply-${auditionId}` });
        }
      }
    } catch {
      toast.error(t('auditions:apply.toastUnexpectedErrorShort'), {
        id: `apply-${auditionId}`,
      });
    } finally {
      setIsApplying(false);
    }
  }, [
    isAuthenticated,
    urlAccessCode,
    effectiveCode,
    slug,
    auditionTitle,
    auditionId,
    brand,
    t,
    router,
  ]);

  // Apply-journey resume (BAL-344): the page arrived with `?apply=true` — the
  // signal carried end-to-end by the anonymous CTA, the "Already have an
  // account? Sign in" link, and the resume banner (`buildApplyJourneyNext`).
  // Sign-in correctly returns the dancer here with the flag set, but until now
  // nothing consumed it, so an authenticated dancer was stranded on a static
  // page and their registration intent was silently dropped (the sign-IN
  // sibling of the sign-UP fix in ccc3f7b76b). This effect closes that last
  // hop: it auto-triggers the same registration the CTA would, exactly once.
  //
  // Guards: fires only for an authenticated, accepting-applications dancer who
  // is not invitation-gated. When a registration already exists it routes to
  // the right surface (wizard / detail / reopen) instead of creating a second.
  // Anonymous viewers are untouched — they still get the sign-in path first.
  const autoApplyFiredRef = useRef(false);
  const isInvitationBlocked =
    byInvitationOnly &&
    viewerInvitationStatus !== 'active' &&
    !existingApplication &&
    // A verified code lifts the gate: auto-apply proceeds and create-application
    // redeems the code (creating the invitation). Revoked stays blocked.
    !(isCodeVerified && viewerInvitationStatus !== 'revoked');

  const routeExistingApplication = useCallback(() => {
    if (!existingApplication) return;
    if (existingApplication.status === 'archived') {
      // Reopen requires an explicit user action (the section renders a Reopen
      // button) — don't auto-mutate an archived application. Leave the dancer
      // on the page where that CTA is visible.
      return;
    }
    const cta = competitionCtaForExistingApplication(
      t,
      existingApplication.status,
      slug,
      existingApplication.id,
      allowAcceptedEdits,
    );
    if (cta) router.push(cta.href);
  }, [existingApplication, slug, t, router, allowAcceptedEdits]);

  useEffect(() => {
    if (!autoApply || autoApplyFiredRef.current) return;
    if (!isAuthenticated || externalLink) return;
    if (isInvitationBlocked) return;
    autoApplyFiredRef.current = true;

    if (existingApplication) {
      routeExistingApplication();
      return;
    }
    if (!isOpen) return;
    // Defer the create call so its first `setIsApplying(true)` doesn't run
    // synchronously inside the effect body (react-hooks/set-state-in-effect).
    // Same async-boundary shape as the access-code auto-redeem effect above.
    void Promise.resolve().then(() => handleApply());
  }, [
    autoApply,
    isAuthenticated,
    externalLink,
    isInvitationBlocked,
    existingApplication,
    isOpen,
    handleApply,
    routeExistingApplication,
  ]);

  // Invitation gate (D8): when the audition is invitation-only, a signed-in
  // dancer without an active invitation cannot apply. We still let them see
  // the page (this branch only collapses the apply CTA). Anonymous viewers
  // are NOT gated here — they get the normal "Apply" → sign-in path, and the
  // server resolves their invitation status post-auth. This avoids
  // revealing invitee identity pre-auth and keeps the page friendly to
  // marketing traffic.
  //
  // An EXISTING application short-circuits the gate. If a dancer is mid-
  // application (e.g. invitation revoked after they started), they keep
  // access to their draft so they can finish or archive cleanly.
  if (
    isAuthenticated &&
    byInvitationOnly &&
    viewerInvitationStatus !== 'active' &&
    !existingApplication &&
    // A client-verified code unlocks the Register CTA (fall through to the
    // normal render); registration then redeems it. Revoked invitations are
    // never re-unlockable by a code.
    !(isCodeVerified && viewerInvitationStatus !== 'revoked')
  ) {
    const caption =
      viewerInvitationStatus === 'revoked'
        ? t('auditions:apply.invitationRevoked')
        : t('auditions:apply.invitationOnly');

    // Offer manual code entry whenever the competition has a code and the
    // viewer is merely uninvited (never for revoked invitations). Unlocking
    // only VERIFIES the code (no dancer profile required); the verified code
    // rides into registration, where create-application redeems it.
    const showCodeEntry = hasAccessCode && viewerInvitationStatus !== 'revoked';

    return (
      <div className="space-y-5">
        <Button
          disabled
          className="bg-foreground/10 text-muted-foreground hover:bg-foreground/10 h-11 w-full disabled:opacity-100"
        >
          {ctaLabel}
        </Button>
        <p
          role="status"
          className="text-muted-foreground text-center text-xs leading-relaxed"
        >
          {caption}
        </p>
        {showCodeEntry && (
          <AccessCodeForm
            value={manualCode}
            onChange={setManualCode}
            onSubmit={handleVerifyManualCode}
            submitting={isVerifyingCode}
            t={t}
          />
        )}
      </div>
    );
  }

  // Existing registration → mirror Meteor's `applicationCanBeEdited`:
  //   open / issues / submitted → wizard URL (submitted stays editable)
  //   archived                  → one-click reopen → wizard
  //   accepted / rejected       → status text only, no CTA
  if (isAuthenticated && existingApplication) {
    const cta = competitionCtaForExistingApplication(
      t,
      existingApplication.status,
      slug,
      existingApplication.id,
      allowAcceptedEdits,
    );

    if (cta === null) {
      return (
        <div className="space-y-5">
          <p
            role="status"
            className="bg-foreground/[0.04] text-foreground/90 rounded-sm px-4 py-3 text-sm"
          >
            {existingApplication.status === 'accepted'
              ? t('auditions:apply.existing.acceptedMessage')
              : t('auditions:apply.existing.rejectedMessage')}
          </p>
        </div>
      );
    }

    if (existingApplication.status === 'archived') {
      return (
        <div className="space-y-5">
          <p
            role="status"
            className="bg-foreground/[0.04] text-foreground/90 rounded-sm px-4 py-3 text-sm"
          >
            {cta.statusLine}
          </p>
          <Button
            className="h-11 w-full"
            variant={cta.variant}
            disabled={isReopening}
            onClick={() => {
              startReopen(async () => {
                const result = await reopenApplicationAction(
                  existingApplication.id,
                );
                if (result.success) {
                  toast.success(t('auditions:apply.toastReopened'));
                  router.push(
                    `/competitions/${slug}/apply?applicationId=${existingApplication.id}`,
                  );
                } else {
                  toast.error(
                    typeof result.error === 'string'
                      ? result.error
                      : 'Failed to reopen application',
                  );
                }
              });
            }}
          >
            {isReopening ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              cta.icon
            )}
            {cta.label}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <p
          role="status"
          className="bg-foreground/[0.04] text-foreground/90 rounded-sm px-4 py-3 text-sm"
        >
          {cta.statusLine}
        </p>
        <Button asChild className="h-11 w-full" variant={cta.variant}>
          <Link href={cta.href}>
            {cta.icon}
            {cta.label}
          </Link>
        </Button>
      </div>
    );
  }

  // Single Register/Apply CTA. Style + category selection moved INTO the
  // registration form (the in-form `competition_styles` + `age_category`
  // fields on /competitions/[slug]/apply), so this page is button-only.
  // (CTA label computed once at the top — see `ctaLabel` above.)

  return (
    <div className="space-y-5">
      {isSafeExternalHref(externalLink) ? (
        <Button asChild className="h-11 w-full">
          <AuditionExternalLink
            auditionId={auditionId}
            linkType="apply"
            href={externalLink}
          >
            <Trans i18nKey="auditions:apply.applyExternal" />
            <ExternalLink className="ml-2 h-4 w-4" />
          </AuditionExternalLink>
        </Button>
      ) : !isAuthenticated ? (
        // Public/anonymous code gate: a code-gated competition keeps the
        // Register CTA disabled (and shows the access-code box) until a valid
        // code is verified — typed here, or carried in via `?code=` and
        // auto-verified on mount. Once verified, the code rides into the signup
        // journey (`effectiveCode`) and auto-redeems post-login.
        requiresPublicCode && !isCodeVerified ? (
          <div className="space-y-5">
            <AnonymousApplyCta
              target={{ surface: 'competition', slug }}
              auditionTitle={auditionTitle}
              ctaLabel={ctaLabel}
              disabled
              hideSignInLink
            />
            <p
              role="status"
              className="text-muted-foreground text-center text-xs leading-relaxed"
            >
              <Trans i18nKey="auditions:apply.invitationOnly" />
            </p>
            <AccessCodeForm
              value={manualCode}
              onChange={setManualCode}
              onSubmit={handleVerifyManualCode}
              submitting={isVerifyingCode}
              t={t}
            />
          </div>
        ) : (
          <AnonymousApplyCta
            target={{
              surface: 'competition',
              slug,
              ...(effectiveCode ? { code: effectiveCode } : {}),
            }}
            auditionTitle={auditionTitle}
            ctaLabel={ctaLabel}
            // BAL-342: single clean "Register for Scholarship" CTA on the
            // competition landing — the sign-in path moves to the sign-up page
            // (its `next`-preserving sign-in link keeps returning users on the
            // apply journey), so no one is stranded.
            hideSignInLink
          />
        )
      ) : !isOpen ? (
        <Button
          disabled
          className="bg-foreground/10 text-muted-foreground hover:bg-foreground/10 h-11 w-full disabled:opacity-100"
        >
          <Trans i18nKey="auditions:apply.applicationsClosed" />
        </Button>
      ) : (
        <Button
          className="disabled:bg-foreground/10 disabled:text-muted-foreground h-11 w-full disabled:opacity-100"
          onClick={handleApply}
          disabled={isApplying}
        >
          {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {ctaLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * Shared access-code entry box. Used in two mutually-exclusive branches — the
 * authenticated invitation gate (redeem path) and the anonymous public gate
 * (verify path) — so they never co-render and the shared input `id` is safe.
 */
function AccessCodeForm({
  value,
  onChange,
  onSubmit,
  submitting,
  t,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  submitting: boolean;
  t: (key: string) => string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
      className="space-y-2"
    >
      <label
        htmlFor="competition-access-code"
        className="text-muted-foreground block text-xs"
      >
        <Trans i18nKey="auditions:apply.accessCodePrompt" />
      </label>
      <div className="flex gap-2">
        <Input
          id="competition-access-code"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('auditions:apply.accessCodePlaceholder')}
          autoComplete="off"
          spellCheck={false}
          disabled={submitting}
        />
        <Button
          type="submit"
          variant="outline"
          disabled={submitting || !value.trim()}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('auditions:apply.unlock')
          )}
        </Button>
      </div>
    </form>
  );
}

type CompetitionButtonVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link';

function competitionCtaForExistingApplication(
  t: (key: string) => string,
  status: string,
  slug: string,
  applicationId: string,
  allowAcceptedEdits: boolean,
): {
  href: string;
  label: string;
  statusLine: string;
  variant: CompetitionButtonVariant;
  icon: React.ReactNode;
} | null {
  // Mirrors Meteor's `applicationCanBeEdited` — open/issues/submitted/
  // archived render a CTA; accepted/rejected are terminal and surface a
  // status line only.
  const wizardHref = `/competitions/${slug}/apply?applicationId=${applicationId}`;
  const detailHref = `/home/auditions/applications/${applicationId}`;

  switch (status) {
    case 'open':
      return {
        href: wizardHref,
        label: t('auditions:apply.existing.openLabel'),
        statusLine: t('auditions:apply.existing.openStatus'),
        variant: 'secondary',
        icon: null,
      };
    case 'issues':
      return {
        href: wizardHref,
        label: t('auditions:apply.existing.issuesLabel'),
        statusLine: t('auditions:apply.existing.issuesStatus'),
        variant: 'default',
        icon: null,
      };
    case 'archived':
      return {
        href: wizardHref, // post-reopen redirects to wizard
        label: t('auditions:apply.existing.archivedLabel'),
        statusLine: t('auditions:apply.existing.archivedStatus'),
        variant: 'default',
        icon: <RotateCcw className="mr-2 h-4 w-4" />,
      };
    case 'accepted':
      // Accepted registrations stay editable when the organizer permits it —
      // the CTA routes back into the wizard, and the status line keeps the
      // acceptance confirmation. When edits are frozen, fall through to the
      // status-only box (null).
      return allowAcceptedEdits
        ? {
            href: wizardHref,
            label: t('auditions:apply.existing.acceptedEditLabel'),
            statusLine: t('auditions:apply.existing.acceptedMessage'),
            variant: 'secondary',
            icon: null,
          }
        : null;
    case 'rejected':
      return null;
    case 'submitted':
    default:
      return {
        href: detailHref,
        label: t('auditions:apply.existing.submittedLabel'),
        statusLine: t('auditions:apply.existing.submittedStatus'),
        variant: 'secondary',
        icon: null,
      };
  }
}
